import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CurrenciesRepository } from '@app/data-layer/repositories';
import { LINE_ITEMS_BULK_DELETE_LIMIT } from '@data-layer/repositories/line-items.repository';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { DateHelper } from '@helpers/date.helper';
import { AlertService } from '@services/alert.service';
import { LineItemsBulkDeleteStrategy } from '@shared/es-mutual/services';
import { take } from 'rxjs/operators';

import { MasterCheckboxState } from '../../../es-line-items-bulk-delete-modal/es-line-items-bulk-delete-modal.component';

@Component({
  selector: 'es-details-invoice-ocr-card-hts-desktop',
  templateUrl: './es-details-invoice-ocr-card-hts-desktop.component.html',
  styleUrls: ['./es-details-invoice-ocr-card-hts-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDetailsInvoiceOcrCardHtsDesktopComponent implements OnInit, OnChanges {
  @Input() public invoice: InvoiceModel;
  @Input() public shipmentExportDate: string;
  @Input() public currencyRate: number;
  @Input() public isEditable: boolean;
  // Additive granular flag (broker page); falls back to `isEditable` when undefined.
  @Input() public canEditLineItems: boolean;
  @Input() public isInvoiceLinesCorrect: boolean;
  // Gate for the multi-select / bulk-delete UI (client + broker pages); admin leaves it unbound.
  // The write is routed via LineItemsBulkDeleteStrategy — the broker page overrides it in providers.
  @Input() public isCanBulkDelete: boolean = false;
  /** Added to the 1-based row number: a host that renders a slice of the invoice passes the real position (#13384 F7). */
  @Input() public indexOffset: number = 0;
  @Output() public addLineItem: EventEmitter<number> = new EventEmitter();
  @Output() public editLineItem: EventEmitter<number> = new EventEmitter();
  @Output() public deleteLineItem: EventEmitter<number> = new EventEmitter();
  @Output() public linesDeleted: EventEmitter<void> = new EventEmitter();

  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;

  public isMultiSelectMode: boolean = false;
  public selectedIds: Set<number> = new Set<number>();
  public isBulkDeleteModalOpen: boolean = false;
  public isBulkDeleting: boolean = false;
  private pendingDeleteIds: number[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private currenciesRepository: CurrenciesRepository,
    private lineItemsBulkDeleteStrategy: LineItemsBulkDeleteStrategy,
    private alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    if (!!this.invoice?.currency && this.invoice.currency !== DEFAULT_CURRENCY_CODE) {
      this.SyncCurrencyRate();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.invoice || changes.invoice.isFirstChange()) {
      return;
    }

    const lines = this.invoice?.lines;
    if (!lines?.length) {
      this.exitMultiSelect();
      return;
    }

    const actualIds = new Set(lines.map((line) => line.id));
    this.selectedIds = new Set([...this.selectedIds].filter((id) => actualIds.has(id)));
  }

  public get masterState(): MasterCheckboxState {
    const total = this.invoice?.lines?.length || 0;
    const selected = this.selectedIds.size;

    if (!selected) {
      return 'unchecked';
    }

    return selected === total ? 'checked' : 'indeterminate';
  }

  public enterMultiSelect(): void {
    this.isMultiSelectMode = true;
    this.selectedIds = new Set<number>();
  }

  public exitMultiSelect(): void {
    this.isMultiSelectMode = false;
    this.selectedIds = new Set<number>();
  }

  public toggleRow(id: number): void {
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectedIds = next;
  }

  public onMasterClick(): void {
    if (this.masterState === 'checked') {
      this.selectedIds = new Set<number>();
      return;
    }

    this.selectAll();
  }

  public selectAll(): void {
    this.selectedIds = new Set((this.invoice?.lines || []).map((line) => line.id));
  }

  public selectNone(): void {
    this.selectedIds = new Set<number>();
  }

  public openBulkDeleteModal(): void {
    if (!this.selectedIds.size) {
      return;
    }

    this.openDeleteModal((line) => this.selectedIds.has(line.id));
  }

  public openRemoveAllModal(): void {
    this.openDeleteModal(() => true);
  }

  public onBulkDeleteConfirmed(): void {
    if (this.isBulkDeleting) {
      return;
    }

    this.isBulkDeleting = true;
    this.lineItemsBulkDeleteStrategy
      .bulkDelete(this.pendingDeleteIds)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isBulkDeleting = false;
          this.isBulkDeleteModalOpen = false;
          this.exitMultiSelect();
          this.linesDeleted.emit();
          this.cdr.markForCheck();
        },
        error: () => {
          // The global error alert is shown by ApiService/ApiErrorService; selection is kept.
          this.isBulkDeleting = false;
          this.isBulkDeleteModalOpen = false;
          this.cdr.markForCheck();
        },
      });
  }

  public onBulkDeleteCancelled(): void {
    this.isBulkDeleteModalOpen = false;
  }

  private openDeleteModal(isIncluded: (line: LineModel) => boolean): void {
    const ids = (this.invoice?.lines || []).filter((line) => isIncluded(line)).map((line) => line.id);

    if (ids.length > LINE_ITEMS_BULK_DELETE_LIMIT) {
      this.alertService.warning('BULK_DELETE_LINE_ITEMS_LIMIT', 'BULK_DELETE_LINE_ITEMS_TITLE', { limit: LINE_ITEMS_BULK_DELETE_LIMIT });
      return;
    }

    this.pendingDeleteIds = ids;
    this.isBulkDeleteModalOpen = true;
  }

  private SyncCurrencyRate() {
    const currency = this.invoice.currency;
    const exportDate = this.shipmentExportDate ? this.shipmentExportDate : DateHelper.now;
    this.currenciesRepository
      .getRate(currency, exportDate)
      .pipe(take(1))
      .subscribe((currencyRate) => {
        this.currencyRate = currencyRate ? currencyRate.rate : NaN;
        this.cdr.detectChanges();
      });
  }

  public unitOfMeasure(lineItem: LineModel): string {
    return `${lineItem.quantity1 || ''}${lineItem.unitOfMeasure1 || ''} ${lineItem.quantity2 ? '/' : ''} ${lineItem.quantity2 || ''}${
      lineItem.unitOfMeasure2 || ''
    } ${lineItem.quantity3 ? '/' : ''} ${lineItem.quantity3 || ''}${lineItem.unitOfMeasure3 || ''}`;
  }

  public codeGroupAt(htsCode: string, charNum: number): string {
    if (!htsCode) {
      return '';
    }

    return htsCode?.[charNum];
  }
}
